import { useEffect, useRef, useState } from 'react';

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

const prefersReducedMotion = (): boolean => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Tweens 0 → target on mount (or whenever target changes) via
 * requestAnimationFrame, easeOutCubic. Jumps straight to target when the
 * user prefers reduced motion or the value is null/NaN.
 */
export function useCountUp(target: number | null | undefined, durationMs: number = 900): number {
    const [value, setValue] = useState<number>(target == null ? 0 : target);
    const rafRef = useRef<number | null>(null);
    const startedAtRef = useRef<number>(0);
    const startValueRef = useRef<number>(0);

    useEffect(() => {
        if (target == null || isNaN(target)) {
            setValue(0);
            return;
        }
        if (prefersReducedMotion()) {
            setValue(target);
            return;
        }

        startedAtRef.current = performance.now();
        startValueRef.current = 0;

        const tick = (now: number) => {
            const elapsed = now - startedAtRef.current;
            const t = Math.min(1, elapsed / durationMs);
            const eased = easeOutCubic(t);
            setValue(startValueRef.current + (target - startValueRef.current) * eased);
            if (t < 1) {
                rafRef.current = requestAnimationFrame(tick);
            }
        };

        rafRef.current = requestAnimationFrame(tick);

        return () => {
            if (rafRef.current != null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [target, durationMs]);

    return value;
}
